import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';

import MaltaLandingHero from '../../components/malta/MaltaLandingHero';
import MaltaBenefitsGrid from '../../components/malta/MaltaBenefitsGrid';
import MaltaTrustStrip from '../../components/malta/MaltaTrustStrip';
import DemioEmbed from '../../components/malta/DemioEmbed';
import MaltaPricingCards from '../../components/malta/MaltaPricingCards';
import MaltaSocialProof from '../../components/malta/MaltaSocialProof';
import MaltaFAQ from '../../components/malta/MaltaFAQ';
import ScrollTracker from '../../components/malta/ScrollTracker';

const MaltaBusinessLanding = () => {
  useEffect(() => {
    // Track page view
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        'page_title': 'Malta Business Landing',
        'page_location': window.location.href,
        'event_category': 'engagement',
        'event_label': 'business'
      });
    }
  }, []);

  const heroData = {
    headline: "Simplify Your Payroll Before the Year-End — We'll Handle Setup for €450",
    subtext: "Stop struggling with complex payroll processes. Our €450 all-inclusive migration & training gets your business ready for 2025 with automated FS5/P30, leave management, and guaranteed compliance.",
    primaryCtaText: "Watch 15-min Demo",
    primaryCtaLink: "#demo",
    secondaryCtaText: "Secure €450 Migration",
    secondaryCtaLink: "/mt/start",
    trustBadges: [
      "Migration + training €450",
      "First live run billed in January", 
      "No long-term contracts"
    ]
  };

  const benefitsData = [
    {
      icon: 'document',
      title: 'Automated FS5/P30 Processing',
      description: 'Generate and submit FS5 and P30 forms automatically. Never miss a deadline or make calculation errors again.'
    },
    {
      icon: 'clock',
      title: 'Bonuses & Leave Automation',
      description: 'Automatically calculate overtime, bonuses, and leave entitlements according to Maltese employment law.'
    },
    {
      icon: 'cloud',
      title: 'Digital Payslip Distribution',
      description: 'Send payslips directly to employees via email or secure portal. No more printing and distributing paper payslips.'
    },
    {
      icon: 'shield',
      title: 'Compliance Guaranteed',
      description: 'Stay up-to-date with Maltese payroll regulations. We handle all tax updates and legal changes automatically.'
    },
    {
      icon: 'users',
      title: 'Employee Self-Service',
      description: 'Let employees manage their own leave requests, view payslips, and update personal details through the employee portal.'
    },
    {
      icon: 'chart',
      title: 'Real-Time Reporting',
      description: 'Generate payroll reports instantly. Track costs, analyze trends, and make informed business decisions.'
    }
  ];

  const pricingData = [
    {
      title: 'Migration & Training',
      description: 'Complete setup and team training',
      price: '450',
      period: 'one-time',
      note: 'Includes data migration, training, and first month',
      features: [
        'Complete data migration from your current system',
        '2-hour team training session',
        'System configuration and setup',
        'First month of service included',
        'Priority support during transition'
      ],
      ctaText: 'Secure Your Spot',
      ctaLink: '/mt/start',
      featured: true
    },
    {
      title: 'Business Plan',
      description: 'Perfect for growing businesses',
      price: '2-3',
      period: '/employee/month',
      note: 'Scales with your team size',
      features: [
        'Up to 200 employees',
        'Employee self-service portal',
        'Automated FS5/P30 generation',
        'Leave management system',
        'Email and chat support',
        'Monthly payroll reports'
      ],
      ctaText: 'Learn More',
      ctaLink: 'https://support.buddy.hr/hc/en-mt/requests/new?ticket_form_id=13159221572114'
    },
    {
      title: 'Enterprise Features',
      description: 'For larger businesses',
      price: 'Custom',
      period: 'pricing',
      features: [
        'Advanced reporting packages',
        'Time tracking integration',
        'HR management features',
        'Custom payslip templates',
        'API access for integrations',
        'Priority phone support'
      ],
      ctaText: 'Contact Sales',
      ctaLink: 'https://support.buddy.hr/hc/en-mt/requests/new?ticket_form_id=13159221572114'
    }
  ];

  const socialProofData = {
    title: "Trusted by Malta's Leading Businesses",
    subtitle: "From startups to enterprises, businesses across Malta rely on Buddy for their payroll",
    stats: {
      businesses: "200+",
      employees: "5,000+",
      years: "5+"
    }
  };

  const testimonials = [
    {
      quote: "Buddy has made payroll so much easier. The automated FS5 generation alone saves us hours every month.",
      name: "Maria Attard",
      role: "HR Manager, Sixteen Ltd",
      avatar: null // TODO: Add actual testimonial images
    },
    {
      quote: "Our employees love the self-service portal. It's reduced our admin workload significantly.",
      name: "James Farrugia",
      role: "Operations Director, Myoka Spas",
      avatar: null
    },
    {
      quote: "The compliance features give us peace of mind. We never worry about missing tax updates anymore.",
      name: "Anna Vella",
      role: "Finance Manager, Sky Spirit",
      avatar: null
    }
  ];

  return (
    <React.Fragment>
      <ScrollTracker pageName="business" />
      <Helmet>
        <title>Switch to Buddy Payroll | €450 Migration for Malta Businesses</title>
        <meta 
          name="description" 
          content="Simplify payroll before year-end. €450 all-inclusive migration & training. Automated FS5/P30, leave management, compliance guaranteed. Ready for January 2025." 
        />
        <link rel="canonical" href="https://www.buddy.hr/mt/business-switch-payroll" />
        <meta property="og:title" content="Switch to Buddy Payroll | €450 Migration for Malta Businesses" />
        <meta property="og:description" content="Simplify payroll before year-end. €450 all-inclusive migration & training. Automated FS5/P30, leave management, compliance guaranteed." />
        <meta property="og:url" content="https://www.buddy.hr/mt/business-switch-payroll" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Switch to Buddy Payroll | €450 Migration for Malta Businesses" />
        <meta name="twitter:description" content="Simplify payroll before year-end. €450 all-inclusive migration & training. Automated FS5/P30, leave management." />
      </Helmet>

      <div className="bg-gray-800">
        <MaltaLandingHero {...heroData} />
      </div>

      <MaltaBenefitsGrid 
        benefits={benefitsData}
        title="Why Businesses Choose Buddy"
      />

      <MaltaTrustStrip />

      <DemioEmbed 
        webinarId={null} // TODO: Add actual Demio webinar ID for businesses
        primaryCtaText="Secure €450 Migration"
        primaryCtaLink="/mt/start"
        secondaryCtaText="Book a 1:1 Call"
        secondaryCtaLink="https://support.buddy.hr/hc/en-mt/requests/new?ticket_form_id=13159221572114"
      />

      <MaltaPricingCards pricingData={pricingData} />

      <MaltaSocialProof 
        testimonials={testimonials}
        stats={socialProofData}
      />

      <MaltaFAQ />

      {/* Final CTA Section */}
      <div className="bg-orange-600">
        <div className="max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
          <h2 className="text-3xl leading-9 font-extrabold text-white sm:text-4xl sm:leading-10">
            <span className="block">Ready to simplify your payroll?</span>
            <span className="block">Limited migration slots before December 15</span>
          </h2>
          <p className="mt-4 text-lg leading-6 text-orange-200">
            Join Malta's leading businesses. No long-term contracts • Cancel anytime
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/mt/start"
              className="w-full sm:w-auto inline-flex items-center justify-center px-5 py-3 border border-transparent text-base leading-6 font-medium rounded-md text-orange-600 bg-white hover:text-orange-500 hover:bg-orange-50 transition duration-150 ease-in-out"
            >
              Secure €450 Migration
            </a>
            <a
              href="https://support.buddy.hr/hc/en-mt/requests/new?ticket_form_id=13159221572114"
              className="w-full sm:w-auto inline-flex items-center justify-center px-5 py-3 border border-white text-base leading-6 font-medium rounded-md text-white hover:bg-white hover:text-orange-600 transition duration-150 ease-in-out"
            >
              Book a 1:1 Call
            </a>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = dispatch => ({});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MaltaBusinessLanding);
